import { Badge, Box, Button, IconButton, Spinner, Text } from '@rocket/ui';
import React from 'react';
import { LuCheck, LuChevronDown, LuChevronUp, LuUndo2, LuX } from 'react-icons/lu';

import { TextDiff, TooltipCustom } from '../';
import {
  getEditType,
  getScoreLabel,
  getSectionNames,
  SIMILARITY_THRESHOLDS,
  SORT_FIELDS,
  STATUS_TYPES,
} from './AIEditsTab.utils';

// biome-ignore lint/suspicious/noExplicitAny: sentence objects come from untyped legacy AI edits data
type AnyData = any;

interface AIEditsTabTableProps {
  filteredAndSortedSentences: Array<AnyData>;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  expandedRows: Set<string>;
  processingIds: Set<string>;
  onSort: (field: string) => void;
  onToggleExpand: (tuid: string) => void;
  onApprove: (sentence: AnyData) => void;
  onDisapprove: (sentence: AnyData) => void;
  onUndo: (sentence: AnyData) => void;
}

const scoreColorPalette = (score: number): 'green' | 'orange' | 'red' => {
  if (score >= SIMILARITY_THRESHOLDS.HIGH) {
    return 'green';
  }
  if (score >= SIMILARITY_THRESHOLDS.MEDIUM) {
    return 'orange';
  }
  return 'red';
};

const AIEditsTabTable = ({
  filteredAndSortedSentences,
  sortBy,
  sortOrder,
  expandedRows,
  processingIds,
  onSort,
  onToggleExpand,
  onApprove,
  onDisapprove,
  onUndo,
}: AIEditsTabTableProps) => {
  const renderSortLabel = (field: string, label: string) => {
    const active = sortBy === field;
    return (
      <Button
        variant="plain"
        size="xs"
        aria-label={`Sort by ${label}`}
        onClick={() => onSort(field)}
        display="inline-flex"
        alignItems="center"
        gap="4px"
        height="auto"
        minWidth="0"
        padding="0"
        color="fg"
        fontWeight="600"
        textTransform="uppercase"
      >
        {label}
        {active && (
          <Box as="span" color="fg.muted" display="inline-flex">
            {sortOrder === 'asc' ? <LuChevronUp size={14} /> : <LuChevronDown size={14} />}
          </Box>
        )}
      </Button>
    );
  };

  return (
    <Box
      role="region"
      aria-label="AI edits review table"
      border="1px solid"
      borderColor="border"
      borderRadius="8px"
      overflow="hidden"
    >
      <Box
        as="table"
        aria-label="Sentences with AI edits for review"
        width="100%"
        borderCollapse="collapse"
      >
        <Box as="thead">
          <Box as="tr" bg="bg.subtle">
            <Box
              as="th"
              textAlign="left"
              color="fg"
              fontWeight="600"
              textTransform="uppercase"
              borderBottom="2px solid"
              borderColor="border"
              padding="12px"
            >
              Original vs Edited Text
            </Box>
            <Box
              as="th"
              width="120px"
              textAlign="left"
              borderBottom="2px solid"
              borderColor="border"
              padding="12px"
            >
              {renderSortLabel(SORT_FIELDS.SIMILARITY, 'Similarity')}
            </Box>
            <Box
              as="th"
              width="140px"
              textAlign="left"
              borderBottom="2px solid"
              borderColor="border"
              padding="12px"
            >
              {renderSortLabel(SORT_FIELDS.SECTION, 'Section')}
            </Box>
            <Box
              as="th"
              width="160px"
              textAlign="left"
              color="fg"
              fontWeight="600"
              textTransform="uppercase"
              borderBottom="2px solid"
              borderColor="border"
              padding="12px"
            >
              Actions
            </Box>
          </Box>
        </Box>

        <Box as="tbody">
          {/* biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy .jsx row render with status/expand branches ported as-is */}
          {filteredAndSortedSentences.map((sentence, index) => {
            const isProcessing = processingIds.has(sentence.tuid);
            const isExpanded = expandedRows.has(sentence.tuid);
            const editType = getEditType(sentence);
            const rowBg = index % 2 === 0 ? 'bg.subtle' : 'bg';
            const score = sentence.score || 0;

            return (
              <React.Fragment key={sentence.tuid}>
                <Box
                  as="tr"
                  bg={rowBg}
                  transition="background-color 0.2s"
                  _hover={{ bg: 'bg.muted' }}
                >
                  <Box
                    as="td"
                    padding="20px 12px"
                    borderBottom={isExpanded ? 'none' : '1px solid'}
                    borderColor="border"
                    verticalAlign="top"
                  >
                    <Box display="flex" alignItems="flex-start" gap="12px">
                      <TooltipCustom title={isExpanded ? 'Hide details' : 'Show details'}>
                        <IconButton
                          size="sm"
                          variant="ghost"
                          onClick={() => onToggleExpand(sentence.tuid)}
                          aria-label={
                            isExpanded ? 'Hide sentence details' : 'Show sentence details'
                          }
                          aria-expanded={isExpanded}
                          color="fg.muted"
                        >
                          {isExpanded ? <LuChevronUp size={18} /> : <LuChevronDown size={18} />}
                        </IconButton>
                      </TooltipCustom>

                      <Box maxWidth="600px" flex="1">
                        <Box marginBottom="8px">
                          <Badge colorPalette="blue" variant="subtle" fontWeight="500">
                            {editType.charAt(0).toUpperCase() + editType.slice(1)}
                          </Badge>
                        </Box>

                        {sentence.edited_value || sentence.edited_text ? (
                          <TextDiff
                            original={(sentence.value || sentence.original_text || '')
                              .replace(/\n+/g, ' ')
                              .replace(/\s{2,}/g, ' ')
                              .trim()}
                            modified={(sentence.edited_value || sentence.edited_text || '')
                              .replace(/\n+/g, ' ')
                              .replace(/\s{2,}/g, ' ')
                              .trim()}
                          />
                        ) : (
                          <Text color="fg" lineHeight="1.6">
                            {sentence.value || sentence.original_text || 'No text available'}
                          </Text>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Box
                    as="td"
                    padding="20px 12px"
                    borderBottom={isExpanded ? 'none' : '1px solid'}
                    borderColor="border"
                    verticalAlign="top"
                  >
                    <Box display="flex" flexDirection="column" gap="4px" alignItems="center">
                      <Badge
                        colorPalette={scoreColorPalette(score)}
                        variant="subtle"
                        fontWeight="600"
                      >
                        {`${Math.round(score * 100)}%`}
                      </Badge>
                      <Text color="fg" lineHeight="1.6" textAlign="center">
                        {getScoreLabel(score)}
                      </Text>
                    </Box>
                  </Box>

                  <Box
                    as="td"
                    padding="20px 12px"
                    borderBottom={isExpanded ? 'none' : '1px solid'}
                    borderColor="border"
                    verticalAlign="top"
                  >
                    <Text
                      color="fg"
                      lineHeight="1.6"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {getSectionNames(sentence).join(', ')}
                    </Text>
                  </Box>

                  <Box
                    as="td"
                    padding="20px 12px"
                    borderBottom={isExpanded ? 'none' : '1px solid'}
                    borderColor="border"
                    verticalAlign="top"
                  >
                    {sentence.status === STATUS_TYPES.APPROVED ? (
                      <Box display="flex" alignItems="center" gap="8px">
                        <Badge colorPalette="green" variant="subtle" fontWeight="600">
                          <LuCheck size={16} />
                          Approved
                        </Badge>
                        <TooltipCustom title="Undo approval">
                          <IconButton
                            size="sm"
                            variant="ghost"
                            onClick={() => onUndo(sentence)}
                            disabled={isProcessing}
                            aria-label="Undo approval"
                            aria-busy={isProcessing}
                            color="fg.muted"
                          >
                            {isProcessing ? <Spinner size="sm" /> : <LuUndo2 size={18} />}
                          </IconButton>
                        </TooltipCustom>
                      </Box>
                    ) : sentence.status === STATUS_TYPES.DISAPPROVED ? (
                      <Box display="flex" alignItems="center" gap="8px">
                        <Badge colorPalette="red" variant="subtle" fontWeight="600">
                          <LuX size={16} />
                          Rejected
                        </Badge>
                        <TooltipCustom title="Undo rejection">
                          <IconButton
                            size="sm"
                            variant="ghost"
                            onClick={() => onUndo(sentence)}
                            disabled={isProcessing}
                            aria-label="Undo rejection"
                            aria-busy={isProcessing}
                            color="fg.muted"
                          >
                            {isProcessing ? <Spinner size="sm" /> : <LuUndo2 size={18} />}
                          </IconButton>
                        </TooltipCustom>
                      </Box>
                    ) : (
                      <Box display="flex" gap="8px">
                        <TooltipCustom title="Approve sentence">
                          <IconButton
                            size="sm"
                            variant="subtle"
                            colorPalette="green"
                            onClick={() => onApprove(sentence)}
                            disabled={isProcessing}
                            aria-label="Approve sentence"
                            aria-busy={isProcessing}
                          >
                            {isProcessing ? <Spinner size="sm" /> : <LuCheck size={18} />}
                          </IconButton>
                        </TooltipCustom>
                        <TooltipCustom title="Reject sentence">
                          <IconButton
                            size="sm"
                            variant="subtle"
                            colorPalette="red"
                            onClick={() => onDisapprove(sentence)}
                            disabled={isProcessing}
                            aria-label="Reject sentence"
                            aria-busy={isProcessing}
                          >
                            {isProcessing ? <Spinner size="sm" /> : <LuX size={18} />}
                          </IconButton>
                        </TooltipCustom>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Collapsible info row */}
                {isExpanded && (
                  <Box as="tr" bg={rowBg}>
                    <Box
                      as="td"
                      {...{ colSpan: 4 }}
                      padding="0 12px"
                      borderBottom="1px solid"
                      borderColor="border"
                    >
                      <Box
                        padding="20px 24px"
                        margin="12px 0"
                        bg="bg.subtle"
                        borderRadius="6px"
                        border="1px solid"
                        borderColor="border"
                      >
                        <Text
                          color="fg"
                          textTransform="uppercase"
                          lineHeight="1.6"
                          fontWeight="600"
                          marginBottom="12px"
                        >
                          Seed Information
                        </Text>

                        <Box display="flex" flexDirection="column" gap="16px">
                          {sentence.sourceText && (
                            <Box>
                              <Text color="fg" fontWeight="600">
                                Seed Sentence
                              </Text>
                              <Text
                                marginTop="6px"
                                color="fg"
                                lineHeight="1.6"
                                padding="12px"
                                bg="bg"
                                borderRadius="4px"
                                border="1px solid"
                                borderColor="border"
                              >
                                {sentence.sourceText}
                              </Text>
                            </Box>
                          )}

                          {sentence.sourceTuid && (
                            <Box>
                              <Text color="fg" fontWeight="600">
                                Training ID
                              </Text>
                              <Text
                                marginTop="6px"
                                color="fg"
                                lineHeight="1.6"
                                padding="12px"
                                bg="bg"
                                borderRadius="4px"
                                border="1px solid"
                                borderColor="border"
                              >
                                {sentence.sourceTuid}
                              </Text>
                            </Box>
                          )}

                          {!sentence.sourceText && !sentence.sourceTuid && (
                            <Text
                              color="fg"
                              textTransform="uppercase"
                              lineHeight="1.6"
                              fontWeight="600"
                            >
                              No seed information available
                            </Text>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(AIEditsTabTable, (prevProps, nextProps) => {
  // Deep comparison for Sets (can't use reference equality)
  const expandedRowsEqual =
    prevProps.expandedRows.size === nextProps.expandedRows.size &&
    [...prevProps.expandedRows].every((id) => nextProps.expandedRows.has(id));

  const processingIdsEqual =
    prevProps.processingIds.size === nextProps.processingIds.size &&
    [...prevProps.processingIds].every((id) => nextProps.processingIds.has(id));

  // Compare arrays by reference (parent should memoize filtered/sorted arrays)
  const sentencesEqual =
    prevProps.filteredAndSortedSentences === nextProps.filteredAndSortedSentences;

  // Compare other props
  const otherPropsEqual =
    prevProps.sortBy === nextProps.sortBy &&
    prevProps.sortOrder === nextProps.sortOrder &&
    prevProps.onSort === nextProps.onSort &&
    prevProps.onToggleExpand === nextProps.onToggleExpand &&
    prevProps.onApprove === nextProps.onApprove &&
    prevProps.onDisapprove === nextProps.onDisapprove &&
    prevProps.onUndo === nextProps.onUndo;

  return expandedRowsEqual && processingIdsEqual && sentencesEqual && otherPropsEqual;
});
